import { supabase } from "@/integrations/supabase/client";
import { queryClient } from "@/lib/queryClient";
import { capture } from "@/lib/sentry/capture";
import type { PostgrestError } from "@supabase/supabase-js";
import { getTranslationsStatic } from "@/lib/i18n/LanguageContext";
import { fill } from "@/lib/i18n/fill";

export type BuildingStorageTier = "free" | "starter" | "growing" | "scale";

export interface BuildingStorageQuota {
  tier: BuildingStorageTier;
  limitBytes: number | null;
  usedBytes: number;
  reservedBytes: number;
  availableBytes: number | null;
  activationPending?: boolean;
  requestedTier?: BuildingStorageTier | null;
}

export interface StorageReservation {
  idempotencyKey: string;
  path: string;
  reservationId: string;
  expiresAt: string;
}

export interface StorageReservationRequest {
  idempotencyKey: string;
  path: string;
  size: number;
}

export class BuildingStorageQuotaError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BuildingStorageQuotaError";
  }
}

const RESERVATION_BATCH_SIZE = 1000;
const RESERVATION_FAILURE_MESSAGE = "Could not reserve building storage. Please try again.";

export function parseBuildingStorageId(value: string | number): number | null {
  if (typeof value === "string" && !/^[1-9]\d*$/.test(value)) return null;
  const parsed = typeof value === "string" ? Number(value) : value;
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : null;
}

const QUOTA_ERRORS = [
  {
    code: "QST01",
    marker: "BUILDING_STORAGE_LIMIT_EXCEEDED",
    message:
      "Building storage is full. Delete files or ask an administrator to increase its storage tier.",
  },
  {
    code: "QST02",
    marker: "BUILDING_STORAGE_RESERVATION_REQUIRED",
    message: "Storage admission is missing. Please try uploading again.",
  },
  {
    code: "QST03",
    marker: "BUILDING_STORAGE_RESERVATION_EXPIRED",
    message: "Storage admission expired. Please try uploading again.",
  },
  {
    code: "QST04",
    marker: "BUILDING_STORAGE_SIZE_MISMATCH",
    message: "Uploaded file size did not match its storage admission. Please try again.",
  },
  {
    code: "QST05",
    marker: "BUILDING_STORAGE_INVALID_DERIVATIVE",
    message: "This file could not be admitted to building storage.",
  },
] as const;

export function storageQuotaErrorMessage(message: string): string | null {
  const sqlstates: string[] = message.match(/\bQST0[1-5]\b/g) ?? [];
  const match = QUOTA_ERRORS.find(
    ({ code, marker }) => sqlstates.includes(code) || message.includes(marker)
  );
  return match?.message ?? null;
}

export function storageQuotaErrorFrom(error: {
  code?: string;
  message?: string;
  details?: string;
}): BuildingStorageQuotaError | null {
  const message =
    storageQuotaErrorMessage(error.code ?? "") ?? storageQuotaErrorMessage(error.message ?? "");
  if (message === null) return null;
  let limit: unknown;
  try {
    const details: unknown = JSON.parse(error.details ?? "{}");
    limit = (details as { limit_bytes?: unknown })?.limit_bytes;
  } catch {
    limit = undefined;
  }
  if (
    typeof limit === "number" &&
    Number.isSafeInteger(limit) &&
    limit >= 0 &&
    (error.code === "QST01" || error.message?.includes("BUILDING_STORAGE_LIMIT_EXCEEDED"))
  ) {
    return new BuildingStorageQuotaError(
      fill(getTranslationsStatic().billing.plans.storageLimit, { limit: formatStorageBytes(limit) })
    );
  }
  return new BuildingStorageQuotaError(message);
}

export function storageAccessErrorFrom(error: {
  code?: string;
  message?: string;
}): BuildingStorageQuotaError | null {
  return error.code === "42501" || error.message?.includes("BUILDING_STORAGE_ACCESS_DENIED")
    ? new BuildingStorageQuotaError("You do not have permission to access this building's storage.")
    : null;
}

function storageInvalidBatchErrorFrom(error: {
  message?: string;
}): BuildingStorageQuotaError | null {
  return error.message?.includes("BUILDING_STORAGE_INVALID_BATCH")
    ? new BuildingStorageQuotaError(
        "These files could not be admitted to building storage. Please try again."
      )
    : null;
}

export function storageAdmissionErrorFrom(error: {
  code?: string;
  message?: string;
  details?: string;
}): BuildingStorageQuotaError | null {
  return (
    storageQuotaErrorFrom(error) ??
    storageAccessErrorFrom(error) ??
    storageInvalidBatchErrorFrom(error)
  );
}

export const buildingStorageQuotaKey = (buildingId: string | number, orgId?: string) => [
  "building-storage-quota",
  String(buildingId),
  orgId ?? "",
];

export function invalidateBuildingStorageQuota(buildingId?: string | number): void {
  void queryClient.invalidateQueries({ queryKey: ["organization-storage-usage"] });
  void queryClient.invalidateQueries({
    queryKey:
      buildingId === undefined
        ? ["building-storage-quota"]
        : ["building-storage-quota", String(buildingId)],
  });
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function byteCount(value: unknown): number | null {
  const parsed =
    typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN;
  return Number.isSafeInteger(parsed) && parsed >= 0 ? parsed : null;
}

function storageTier(value: unknown): BuildingStorageTier | null {
  return value === "free" || value === "starter" || value === "growing" || value === "scale"
    ? value
    : null;
}

function quotaLimitsValid(
  tier: BuildingStorageTier,
  limit: number | null,
  available: number | null
): boolean {
  return tier === "scale"
    ? limit === null && available === null
    : limit !== null && available !== null;
}

function parseQuota(value: unknown): BuildingStorageQuota {
  const row = record(Array.isArray(value) ? value[0] : value);
  const tier = storageTier(row?.tier);
  const usedBytes = byteCount(row?.used_bytes);
  const reservedBytes = byteCount(row?.reserved_bytes);
  const limitBytes = row?.limit_bytes === null ? null : byteCount(row?.limit_bytes);
  const availableBytes = row?.available_bytes === null ? null : byteCount(row?.available_bytes);
  const activationPending = row?.activation_pending === true;
  const requestedTier = row?.requested_tier === null ? null : storageTier(row?.requested_tier);
  const capacityTier =
    tier === "scale" && requestedTier && requestedTier !== "scale" ? requestedTier : tier;
  if (
    tier === null ||
    usedBytes === null ||
    reservedBytes === null ||
    (row?.activation_pending !== undefined && typeof row.activation_pending !== "boolean") ||
    (row?.requested_tier !== undefined && row.requested_tier !== null && requestedTier === null) ||
    (activationPending
      ? limitBytes !== 0 || availableBytes !== 0
      : capacityTier === null || !quotaLimitsValid(capacityTier, limitBytes, availableBytes))
  ) {
    throw new Error("Invalid building storage quota response");
  }
  return {
    tier,
    limitBytes,
    usedBytes,
    reservedBytes,
    availableBytes,
    ...(row?.activation_pending !== undefined ? { activationPending } : {}),
    ...(row?.requested_tier !== undefined ? { requestedTier } : {}),
  };
}

export async function getBuildingStorageQuota(
  buildingId: string | number,
  signal?: AbortSignal
): Promise<BuildingStorageQuota> {
  const parsedBuildingId = parseBuildingStorageId(buildingId);
  if (parsedBuildingId === null) throw new Error("Invalid building ID");
  const query = supabase.rpc("get_building_storage_quota", { p_building_id: parsedBuildingId });
  const { data, error } = await (signal ? query.abortSignal(signal) : query);
  try {
    if (error && storageAccessErrorFrom(error)) {
      throw new BuildingStorageQuotaError(
        "You do not have access to this building's storage usage."
      );
    }
    if (error) throw error;
    return parseQuota(data);
  } catch (failure) {
    if (!(failure instanceof BuildingStorageQuotaError)) {
      capture(failure instanceof Error ? failure : new Error(String(failure)), {
        tags: { feature: "building-storage-quota", stage: "read" },
        fingerprint: ["building-storage-quota", "read"],
        context: { quota: { buildingId: parsedBuildingId } },
      });
    }
    throw failure;
  }
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.length > 0;
}

function parseReservation(value: unknown): StorageReservation {
  const row = record(value);
  if (
    row === null ||
    !nonEmptyString(row.idempotency_key) ||
    !nonEmptyString(row.path) ||
    !nonEmptyString(row.reservation_id) ||
    !nonEmptyString(row.expires_at)
  ) {
    throw new Error("Invalid storage reservation response");
  }
  return {
    idempotencyKey: row.idempotency_key,
    path: row.path,
    reservationId: row.reservation_id,
    expiresAt: row.expires_at,
  };
}

function quotaErrorAmounts(details: string): {
  requested: number | null;
  available: number | null;
} {
  let value: unknown;
  try {
    value = JSON.parse(details);
  } catch {
    value = null;
  }
  const amounts = record(value);
  return {
    requested: byteCount(amounts?.requested_bytes),
    available: byteCount(amounts?.available_bytes),
  };
}

function quotaAmountSuffix(details: string): string | null {
  const { requested, available } = quotaErrorAmounts(details);
  if (requested === null || available === null) return null;
  return fill(getTranslationsStatic().billing.plans.storageRequested, {
    requested: formatStorageBytes(requested),
    available: formatStorageBytes(available),
  });
}

function reservationQuotaError(error: PostgrestError): BuildingStorageQuotaError | null {
  const quotaError = storageAdmissionErrorFrom(error);
  if (quotaError === null) return null;
  const suffix = quotaAmountSuffix(error.details ?? "");
  return new BuildingStorageQuotaError(
    suffix === null ? quotaError.message : `${quotaError.message} ${suffix}`
  );
}

function raiseReservationError(error: PostgrestError, buildingId: number): never {
  const quotaError = reservationQuotaError(error);
  if (quotaError) {
    invalidateBuildingStorageQuota(buildingId);
    throw quotaError;
  }
  throw error;
}

function reservationKey(file: Pick<StorageReservation, "idempotencyKey" | "path">): string {
  return JSON.stringify([file.idempotencyKey, file.path]);
}

function validReservationPath(path: string, buildingId: number): boolean {
  if (!path) return false;
  const segments = path.split("/");
  return segments.length >= 3 && segments[1] === String(buildingId);
}

function validateReservationRequests(buildingId: number, files: StorageReservationRequest[]): void {
  const keys = new Set<string>();
  for (const file of files) {
    const key = reservationKey(file);
    if (
      !file.idempotencyKey ||
      !validReservationPath(file.path, buildingId) ||
      !Number.isSafeInteger(file.size) ||
      file.size < 0 ||
      keys.has(key)
    ) {
      throw new BuildingStorageQuotaError("Invalid storage upload selection. Please try again.");
    }
    keys.add(key);
  }
}

function parseReservations(
  value: unknown,
  files: StorageReservationRequest[]
): StorageReservation[] {
  const rows = Array.isArray(value) ? value : null;
  const reservations = rows?.map(parseReservation);
  const expected = new Set(files.map(reservationKey));
  const returned = new Set(reservations?.map(reservationKey));
  if (
    !reservations ||
    reservations.length !== files.length ||
    returned.size !== expected.size ||
    reservations.some((row) => !expected.has(reservationKey(row))) ||
    new Set(reservations.map((row) => row.reservationId)).size !== reservations.length
  ) {
    throw new Error("Invalid storage reservation response");
  }
  const byRequest = new Map(reservations.map((row) => [reservationKey(row), row]));
  return files.map((file) => {
    const reservation = byRequest.get(reservationKey(file));
    if (!reservation) throw new Error("Invalid storage reservation response");
    return reservation;
  });
}

function returnedReservationIds(value: unknown): string[] {
  const rows = Array.isArray(value) ? value : [value];
  return rows.flatMap((row) => {
    const id = record(row)?.reservation_id;
    return typeof id === "string" && id ? [id] : [];
  });
}

function assertAllReservationsHeld(heldIds: Set<string>, fileCount: number): void {
  if (heldIds.size !== fileCount) throw new Error("Invalid storage reservation response");
}

async function cancelReturnedReservations(ids: Set<string>, buildingId: number): Promise<void> {
  const allIds = [...ids];
  for (let offset = 0; offset < allIds.length; offset += 20) {
    const results = await Promise.allSettled(
      allIds.slice(offset, offset + 20).map(async (reservationId) => {
        const { error } = await supabase.rpc("cancel_building_storage_upload", {
          p_reservation_id: reservationId,
        });
        if (error) throw error;
      })
    );
    for (const result of results) {
      if (result.status === "rejected") {
        capture(result.reason instanceof Error ? result.reason : new Error(String(result.reason)), {
          tags: { feature: "building-storage-quota", stage: "cancel-reserve" },
          fingerprint: ["building-storage-quota", "cancel-reserve"],
          context: { quota: { buildingId } },
        });
      }
    }
  }
  if (ids.size > 0) invalidateBuildingStorageQuota(buildingId);
}

export async function reserveBuildingStorageUploads(
  buildingId: string | number,
  files: StorageReservationRequest[]
): Promise<StorageReservation[]> {
  if (files.length === 0) return [];
  const parsedBuildingId = parseBuildingStorageId(buildingId);
  if (parsedBuildingId === null) {
    throw new BuildingStorageQuotaError("Invalid building ID. Please try again.");
  }
  validateReservationRequests(parsedBuildingId, files);
  const heldIds = new Set<string>();
  const reservations: StorageReservation[] = [];
  try {
    for (let offset = 0; offset < files.length; offset += RESERVATION_BATCH_SIZE) {
      const batch = files.slice(offset, offset + RESERVATION_BATCH_SIZE);
      const { data, error } = await supabase.rpc("reserve_building_storage_uploads", {
        p_building_id: parsedBuildingId,
        p_files: batch.map((file) => ({
          idempotency_key: file.idempotencyKey,
          path: file.path,
          size: file.size,
        })),
      });
      returnedReservationIds(data).forEach((id) => heldIds.add(id));
      if (error) raiseReservationError(error, parsedBuildingId);
      reservations.push(...parseReservations(data, batch));
    }
    assertAllReservationsHeld(heldIds, files.length);
    invalidateBuildingStorageQuota(parsedBuildingId);
    return reservations;
  } catch (failure) {
    await cancelReturnedReservations(heldIds, parsedBuildingId);
    if (failure instanceof BuildingStorageQuotaError) throw failure;
    capture(failure instanceof Error ? failure : new Error(String(failure)), {
      tags: { feature: "building-storage-quota", stage: "reserve" },
      fingerprint: ["building-storage-quota", "reserve"],
      context: { quota: { buildingId: parsedBuildingId, fileCount: files.length } },
    });
    throw new BuildingStorageQuotaError(RESERVATION_FAILURE_MESSAGE);
  }
}

export async function cancelBuildingStorageUpload(
  reservationId: string,
  buildingId: number
): Promise<void> {
  const { error } = await supabase.rpc("cancel_building_storage_upload", {
    p_reservation_id: reservationId,
  });
  if (error) throw error;
  invalidateBuildingStorageQuota(buildingId);
}

export function formatStorageBytes(bytes: number): string {
  if (bytes >= 1_000_000_000) return `${(bytes / 1_000_000_000).toFixed(1).replace(/\.0$/, "")} GB`;
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1).replace(/\.0$/, "")} MB`;
  if (bytes >= 1_000) return `${Math.round(bytes / 1_000)} kB`;
  return `${bytes} B`;
}
